import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, money, fmtDate, CLIENT_TYPE_LABELS, ApiError } from '../api'
import { Page } from '../App'
import { Card, Badge, Alert, Spinner, Empty, Stat, Modal, Field } from '../components/ui'
import { ClientForm, type Client } from './Clients'

interface Detail {
  client: Client
  contacts: Array<{ id: string; name: string | null; email: string; role: string; is_primary: number }>
  invoices: Array<{
    id: string; number: string | null; doc_type: string; issue_date: string; due_date: string
    currency: string; total: number; balance: number; status: string
  }>
  payments: Array<{ id: string; paid_on: string; amount: number; tds_amount: number; method: string; reference_utr: string | null }>
  stats: { lifetime_billed: number; outstanding: number; overdue_count: number; avg_days_to_pay: number | null }
}

export default function ClientDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Detail | null>(null)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [addingContact, setAddingContact] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const load = async () => {
    try {
      setData(await api.get<Detail>(`/clients/${id}`))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load client')
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (error) return <Page title="Client"><Alert kind="error">{error}</Alert></Page>
  if (!data) return <Page title="Client"><Spinner /></Page>

  const c = data.client

  return (
    <Page
      title={c.display_name || c.legal_name}
      subtitle={
        <>
          {CLIENT_TYPE_LABELS[c.type]} {c.gstin && <>· GSTIN {c.gstin}</>} · Net {c.payment_terms_days} days
        </>
      }
      actions={
        <>
          <button className="btn" onClick={() => setDownloading(true)}>
            Download all invoices
          </button>
          <button className="btn" onClick={() => setEditing(true)}>
            Edit client
          </button>
          <button className="btn primary" onClick={() => navigate(`/invoices/new?client=${c.id}`)}>
            + New invoice
          </button>
        </>
      }
    >
      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Stat label="Lifetime billed" value={money(data.stats.lifetime_billed, c.currency)} />
        <Stat
          label="Outstanding"
          value={money(data.stats.outstanding, c.currency)}
          tone={data.stats.outstanding > 0 ? 'danger' : 'ok'}
        />
        <Stat label="Overdue invoices" value={data.stats.overdue_count} />
        <Stat
          label="Average days to pay"
          value={data.stats.avg_days_to_pay ? Math.round(data.stats.avg_days_to_pay) : '—'}
          note="From invoice date to payment"
        />
      </div>

      <div className="grid c2" style={{ marginBottom: 16 }}>
        <Card title="Billing address">
          <div style={{ whiteSpace: 'pre-line' }}>
            {[c.billing_line1, c.billing_line2, [c.billing_city, c.billing_state, c.billing_pincode].filter(Boolean).join(', '), c.billing_country]
              .filter(Boolean)
              .join('\n') || <span className="muted">No address on record</span>}
          </div>
          <div className="sep" />
          <div className="small">
            {c.email && <div>{c.email}</div>}
            {c.phone && <div>{c.phone}</div>}
            {c.reminders_muted ? <div style={{ marginTop: 6 }}><Badge status="draft">Reminders muted</Badge></div> : null}
          </div>
        </Card>

        <Card
          title="Email contacts"
          actions={
            <button className="btn sm" onClick={() => setAddingContact(true)}>
              + Add
            </button>
          }
          tight
        >
          {data.contacts.length === 0 ? (
            <div className="empty small">
              No contacts. Invoices go to the client email above.
            </div>
          ) : (
            <table className="tbl">
              <tbody>
                {data.contacts.map((ct) => (
                  <tr key={ct.id}>
                    <td>
                      <div>{ct.email}</div>
                      {ct.name && <div className="muted small">{ct.name}</div>}
                    </td>
                    <td className="right">
                      <Badge status="neutral">{ct.role.toUpperCase()}</Badge>
                    </td>
                    <td className="right">
                      <button
                        className="btn sm danger"
                        onClick={async () => {
                          await api.del(`/clients/${c.id}/contacts/${ct.id}`)
                          load()
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="Ledger" tight>
        {data.invoices.length === 0 ? (
          <Empty icon="▤" title="No invoices for this client yet" />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Due</th>
                  <th className="r">Total</th>
                  <th className="r">Balance</th>
                  <th className="r">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id} className="clickable" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <td className="mono nowrap">{i.number ?? <span className="muted">Draft</span>}</td>
                    <td className="nowrap small">{fmtDate(i.issue_date)}</td>
                    <td className="nowrap small">{fmtDate(i.due_date)}</td>
                    <td className="num">{money(i.total, i.currency)}</td>
                    <td className="num">{i.balance > 0 ? <strong>{money(i.balance, i.currency)}</strong> : '—'}</td>
                    <td className="right">
                      <Badge status={i.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ClientForm
          client={c}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            load()
          }}
        />
      )}

      {downloading && (
        <DownloadInvoices
          clientId={c.id}
          clientName={c.display_name || c.legal_name}
          invoiceCount={data.invoices.filter((i) => i.status !== 'draft').length}
          onClose={() => setDownloading(false)}
        />
      )}

      {addingContact && (
        <ContactForm
          clientId={c.id}
          onClose={() => setAddingContact(false)}
          onSaved={() => {
            setAddingContact(false)
            load()
          }}
        />
      )}
    </Page>
  )
}

function ContactForm({
  clientId,
  onClose,
  onSaved,
}: {
  clientId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('to')
  const [error, setError] = useState('')

  return (
    <Modal
      title="Add contact"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={async () => {
              try {
                await api.post(`/clients/${clientId}/contacts`, { email, name: name || null, role })
                onSaved()
              } catch (e) {
                setError(e instanceof ApiError ? e.message : 'Failed')
              }
            }}
          >
            Add contact
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>
      <Field label="Email address">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </Field>
      <Field label="Name">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Role" hint="TO gets the invoice, CC is copied, BCC is copied invisibly.">
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="to">To</option>
          <option value="cc">CC</option>
          <option value="bcc">BCC</option>
        </select>
      </Field>
    </Modal>
  )
}

/**
 * Bulk download. The request streams back a ZIP, so it is fetched rather than
 * linked: a plain <a download> cannot send the session cookie's CSRF-free GET
 * with credentials and still surface an error body when something goes wrong.
 */
function DownloadInvoices({
  clientId,
  clientName,
  invoiceCount,
  onClose,
}: {
  clientId: string
  clientName: string
  invoiceCount: number
  onClose: () => void
}) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const download = async () => {
    setError('')
    setBusy(true)
    try {
      const q = new URLSearchParams()
      if (from) q.set('from', from)
      if (to) q.set('to', to)
      if (includeCancelled) q.set('cancelled', '1')

      const res = await fetch(`/api/clients/${clientId}/invoices.zip?${q}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) {
        const ct = res.headers.get('content-type') ?? ''
        const msg = ct.includes('json')
          ? ((await res.json()) as { error?: string }).error
          : await res.text()
        throw new Error(msg || `Download failed (${res.status})`)
      }

      const skipped = Number(res.headers.get('x-invoice-skipped') ?? '0')
      const blob = await res.blob()
      const name =
        res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
        'invoices.zip'

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.click()
      URL.revokeObjectURL(url)

      if (skipped > 0) {
        setError(
          `Downloaded, but ${skipped} invoice${skipped === 1 ? '' : 's'} had no stored PDF and ` +
            `${skipped === 1 ? 'was' : 'were'} listed in MISSING.txt inside the zip.`,
        )
        setBusy(false)
        return
      }
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed')
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Download invoices — ${clientName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={download} disabled={busy}>
            {busy ? 'Preparing…' : 'Download ZIP'}
          </button>
        </>
      }
    >
      <Alert kind={error ? 'warn' : 'info'}>
        {error || `${invoiceCount} issued invoice${invoiceCount === 1 ? '' : 's'} on record. ` +
          'The zip contains one PDF per invoice plus a ledger.csv summary.'}
      </Alert>

      <div className="grid c2">
        <Field label="From date" hint="Leave blank for everything.">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To date">
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>

      <label className="check">
        <input
          type="checkbox"
          checked={includeCancelled}
          onChange={(e) => setIncludeCancelled(e.target.checked)}
        />
        Include cancelled invoices
      </label>
    </Modal>
  )
}
